import { Autocomplete, TextField } from "@mui/material";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

/** United States zones, listed first (R22): one per distinct set of clock rules, east to west, then territories. */
export const US_TIME_ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Phoenix",
  "America/Los_Angeles",
  "America/Anchorage",
  "America/Adak",
  "Pacific/Honolulu",
  "America/Puerto_Rico",
  "Pacific/Guam",
  "Pacific/Pago_Pago",
] as const;

export type UsTimeZone = (typeof US_TIME_ZONES)[number];

/** Copy key under `district.usTimeZone` for each US zone's plain-language name. */
const US_TIME_ZONE_KEY: Record<UsTimeZone, string> = {
  "America/New_York": "eastern",
  "America/Chicago": "central",
  "America/Denver": "mountain",
  "America/Phoenix": "arizona",
  "America/Los_Angeles": "pacific",
  "America/Anchorage": "alaska",
  "America/Adak": "aleutian",
  "Pacific/Honolulu": "hawaii",
  "America/Puerto_Rico": "atlantic",
  "Pacific/Guam": "chamorro",
  "Pacific/Pago_Pago": "samoa",
};

/** "Central Time (Chicago)" for a US zone. */
export const usTimeZoneName = (t: TFunction, zone: UsTimeZone) =>
  t(`district.usTimeZone.${US_TIME_ZONE_KEY[zone]}`);

const US = new Set<string>(US_TIME_ZONES);

/** The browser's IANA zones, or none where `Intl.supportedValuesOf` is missing (older browsers, jsdom). */
const supportedTimeZones = (): string[] => {
  const supportedValuesOf = (
    Intl as unknown as { supportedValuesOf?: (key: string) => string[] }
  ).supportedValuesOf;
  if (typeof supportedValuesOf !== "function") return [];
  try {
    return supportedValuesOf("timeZone");
  } catch {
    return [];
  }
};

/**
 * Options for the time zone field: US zones first, then every zone the browser knows, then the
 * current value when the browser doesn't list it (so a saved zone never disappears).
 */
export const timeZoneOptions = (current?: string | null): string[] => {
  const rest = supportedTimeZones().filter((zone) => !US.has(zone));
  const options = [...US_TIME_ZONES, ...rest];
  return current && !options.includes(current)
    ? [...options, current]
    : options;
};

/** "America/Los_Angeles" → "America/Los Angeles". */
export const timeZoneLabel = (zone: string) => zone.replace(/_/g, " ");

export interface TimeZoneFieldProps {
  /** The chosen IANA zone; null when none is set. */
  value: string | null;
  /** Called with the chosen zone, or null when the field is cleared. */
  onChange: (value: string | null) => void;
  label: string;
  /** A refusal for this field, such as `INVALID_TIMEZONE` copy. */
  error?: string | null;
  helperText?: string;
  disabled?: boolean;
  /** `data-testid` of the input; `district-timezone` by default. */
  testId?: string;
}

/**
 * Time zone picker (FR-041, R22): an Autocomplete over recognized IANA zones, clearable. The server
 * checks the zone again when saving (`INVALID_TIMEZONE`), since browser and server lists differ.
 */
export const TimeZoneField = ({
  value,
  onChange,
  label,
  error,
  helperText,
  disabled = false,
  testId = "district-timezone",
}: TimeZoneFieldProps) => {
  const { t } = useTranslation();
  const options = useMemo(() => timeZoneOptions(value), [value]);
  return (
    <Autocomplete
      options={options}
      value={value}
      onChange={(_event, next) => onChange(next ?? null)}
      getOptionLabel={timeZoneLabel}
      groupBy={(zone) =>
        US.has(zone)
          ? t("district.timezoneGroupUs")
          : t("district.timezoneGroupAll")
      }
      disabled={disabled}
      autoHighlight
      clearOnEscape
      clearText={t("district.timezoneClear")}
      openText={t("district.timezoneOpen")}
      closeText={t("common.close")}
      noOptionsText={t("district.timezoneNoMatch")}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          error={Boolean(error)}
          helperText={error || helperText}
          inputProps={{ ...params.inputProps, "data-testid": testId }}
        />
      )}
    />
  );
};

export interface UsTimeZoneSelectProps {
  /** The chosen IANA zone; "" for the empty choice. */
  value: string;
  onChange: (value: string) => void;
  label: string;
  /** Label of an empty first choice (value ""), such as following the district; none when omitted. */
  emptyLabel?: string;
  /** A refusal for this field, such as `INVALID_TIMEZONE` copy. */
  error?: string | null;
  helperText?: string;
  disabled?: boolean;
  /** `data-testid` of the select. */
  testId: string;
}

/**
 * A native select over the US zones by plain-language name. A saved zone outside the list stays
 * listed under its IANA name, so opening and saving the form never changes it.
 */
export const UsTimeZoneSelect = ({
  value,
  onChange,
  label,
  emptyLabel,
  error,
  helperText,
  disabled = false,
  testId,
}: UsTimeZoneSelectProps) => {
  const { t } = useTranslation();
  return (
    <TextField
      select
      label={label}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      error={Boolean(error)}
      helperText={error || helperText}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: true }}
      inputProps={{ "data-testid": testId }}
    >
      {emptyLabel !== undefined && <option value="">{emptyLabel}</option>}
      {US_TIME_ZONES.map((zone) => (
        <option key={zone} value={zone}>
          {usTimeZoneName(t, zone)}
        </option>
      ))}
      {value && !US.has(value) && (
        <option value={value}>{timeZoneLabel(value)}</option>
      )}
    </TextField>
  );
};

export default TimeZoneField;
